import { useState, useEffect, useContext, useMemo } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import { getBalanceAmendmentTypes, amendBalance } from '../../../api/settings';
import { StoreContext } from '../../../components/Store';

const FormField = ({ label, name, value, disabled, onChange, onBlur, options, isSelect, required, error }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Box sx={{ width: 150 }}>
        {label} {required && <>*</>}
      </Box>
      <Box sx={{ flex: 1 }}>
        <TextField
          id="outlined-basic"
          variant="outlined"
          name={name}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          disabled={disabled}
          fullWidth
          select={isSelect}
          error={error}
          helperText={error}
        >
          {options ? (
            options.map((option) => {
              return (
                <MenuItem key={option.typeId} value={option.typeId}>
                  {option.typeName}
                </MenuItem>
              );
            })
          ) : (
            <></>
          )}
        </TextField>
      </Box>
    </Box>
  );
};

const RequestUnitsModal = ({ isOpen, setIsOpen, companyId, currentBalance, setBalance }) => {
  const store = useContext(StoreContext);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const [amendmentTypes, setAmendmentTypes] = useState([]);
  const [formDetails, setFormDetails] = useState({});
  const [formErrors, setFormErrors] = useState({});

  const formValidation = useMemo(() => {
    const numberValidations = {
      10: 'positive',
      20: 'negative',
      30: 'positive',
      40: 'anyNumber',
    };

    return {
      type: ['required'],
      quantity: ['required', numberValidations[formDetails.type]],
      comments: ['required'],
    };
  }, [formDetails.type]);

  const GENERIC_ERROR = 'An unknown error occurred. Please try again later.';

  // initiailize the modal once it has opened
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getBalanceAmendmentTypes();
        setAmendmentTypes(data);
      } catch (err) {
        setError(GENERIC_ERROR);
        store.showError('Fetching amendment type data. ' + err.message);
      }
    };

    if (isOpen && !amendmentTypes.length) {
      fetchData();
    }

    setIsSubmitting(false);
    setIsSuccess(false);
    setError('');
    setFormDetails({});
    setFormErrors({});
  }, [isOpen]);

  // handle form input change
  const handleChange = (e) => {
    const { name, value } = e.target;

    // for quantity, allow numbers and - symbol at beginning for negatives (also trim leading zeroes)
    const newValue =
      name === 'quantity'
        ? value
            .replace(/[^0-9-]/g, '')
            .replace(/(?!^)-/g, '')
            .replace(/^(-?)0+(\d)/, '$1$2')
        : value;
    setFormDetails((prev) => ({ ...prev, [name]: newValue }));
  };

  // handle submitting the form
  const handleSubmit = async () => {
    const isValid = validateForm();

    if (isValid) {
      setIsSubmitting(true);

      try {
        const [data] = await amendBalance({
          amendmentTypeId: formDetails.type,
          quantity: formDetails.quantity,
          comments: formDetails.comments,
          companyId,
        });
        if (data.message === 'Data saved successfully') {
          if (data.newBalance) {
            setBalance(data.newBalance);
          }

          setIsSuccess(true);
          setIsSubmitting(false);
        }
      } catch (err) {
        setError(GENERIC_ERROR);
        store.showError('Submitting request. ' + err.message);
        setIsSubmitting(false);
      }
    }
  };

  const validateForm = () => {
    let hasErrors = false;
    setFormErrors({});

    const fields = Object.keys(formValidation);
    fields.forEach((field) => {
      const validations = formValidation[field];
      validations.forEach((validation) => {
        let errorMessage = '';
        const value = formDetails[field];

        if (validation === 'required' && !value) {
          errorMessage = 'This field is required';
          hasErrors = true;
        } else if (validation === 'positive') {
          if (parseInt(value) <= 0) {
            errorMessage = 'Must be numeric and greater than 0.';
            hasErrors = true;
          } else if (parseInt(value) > 25000) {
            errorMessage = 'Must be numeric and less than 25,000.';
            hasErrors = true;
          }
        } else if (validation === 'negative') {
          if (parseInt(value) >= 0) {
            errorMessage = 'Must be numeric and less than 0.';
            hasErrors = true;
          } else if (parseInt(value) < -25000) {
            errorMessage = 'Must be numeric and greater than -25,000.';
            hasErrors = true;
          }
        } else if (validation === 'anyNumber') {
          const num = parseInt(value);
          if (num < -25000 || num > 25000) {
            errorMessage = 'Must be numeric and between -25,000 and +25,000';
            hasErrors = true;
          }
        }

        if (errorMessage) {
          setFormErrors((prev) => ({ ...prev, [field]: errorMessage }));
        }
      });
    });

    return !hasErrors;
  };

  const newBalance = currentBalance + parseInt(formDetails?.quantity || '0');

  return (
    <Modal
      open={isOpen}
      onClose={() => !isSubmitting && setIsOpen(false)}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 650,
          bgcolor: 'background.paper',
          border: 'none',
          outline: 'none',
          p: 4,
          borderRadius: 5,
        }}
      >
        <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ mb: 4 }}>
          Amend Balance
        </Typography>

        {isSuccess ? (
          <>
            <Alert sx={{ marginTop: 2, marginBottom: 4 }}>Balance has been amended successfully.</Alert>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <span></span>
              <Button variant="contained" onClick={() => setIsOpen(false)} disabled={isSubmitting}>
                Close
              </Button>
            </Box>
          </>
        ) : (
          <>
            {error && (
              <Box sx={{ paddingBottom: 4 }}>
                <Alert severity="error">{error}</Alert>
              </Box>
            )}

            <FormField
              label="Current Balance"
              name="currentBalance"
              onChange={handleChange}
              value={currentBalance}
              disabled
            />
            <FormField
              label="Type"
              name="type"
              onChange={handleChange}
              value={formDetails.type}
              disabled={isSubmitting}
              options={amendmentTypes}
              isSelect
              required
              error={formErrors.type}
            />
            <FormField
              label="Quantity"
              name="quantity"
              value={formDetails.quantity}
              onChange={handleChange}
              disabled={isSubmitting}
              required
              error={formErrors.quantity}
            />
            <FormField
              label="Comments"
              name="comments"
              value={formDetails.comments}
              onChange={handleChange}
              disabled={isSubmitting}
              required
              error={formErrors.comments}
            />
            <FormField label="New Balance" name="newBalance" onChange={handleChange} value={newBalance} disabled />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button onClick={() => setIsOpen(false)} disabled={isSubmitting}>
                Cancel
              </Button>

              <Button variant="contained" loading={isSubmitting} onClick={handleSubmit} disabled={isSubmitting}>
                Submit
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default RequestUnitsModal;
